import {
  Filter,
  FilterType,
  FilterValue,
  InputType,
  StringArrayFilterValue,
  StringFilterValue,
} from "../../types/filter";
import { TrackingProps } from "../../types/component";
import { useTranslation } from "@coworker/locales";
import AppliedFilterRowCss from "./AppliedFilterRow.module.css";
import { FilterRowScrollPanel } from "../FilterRowScrollPanel";
import { AppliedSelectPill } from "./AppliedSelectPill";
import { AppliedMultiSelectPill } from "./AppliedMultiSelectPill";
import { AppliedCheckboxPill } from "./AppliedCheckboxPill";
import { isInputFilter } from "../../utils/filter/filter";
import { ResetFilters } from "../ResetFilters/ResetFilters";

interface AppliedFilterRowProps {
  filters: Filter[];
  filterValues: FilterValue[];
  setFilterValues: (filterValues: FilterValue[]) => void;
  tracking: TrackingProps;
}

export const AppliedFilterRow = ({
  filters,
  filterValues,
  setFilterValues,
  tracking,
}: AppliedFilterRowProps) => {
  const { t } = useTranslation();

  const appliedFilterCss = [AppliedFilterRowCss["applied-filter-container"]];
  if (filterValues.length === 0) {
    appliedFilterCss.push(AppliedFilterRowCss["applied-filter-display-none"]);
  }

  const hasActiveFilters = filters
    .flatMap((filter) => (isInputFilter(filter) ? [filter] : filter.filters))
    .some(
      (filter) =>
        filter.enabled === true &&
        filter.filterType !== FilterType.Sort &&
        filterValues.some((value) => value.filterId === filter.id)
    );

  if (!hasActiveFilters) return null;

  return (
    <div className={appliedFilterCss.join(" ")}>
      <span className={AppliedFilterRowCss["applied-filter-text"]}>
        {t("appliedFiltersString")}
      </span>
      <FilterRowScrollPanel>
        <ResetFilters
          tracking={tracking}
          resetFilters={() => setFilterValues([])}
        />
        {filters
          // Put all grouped filters in the main filter list
          .flatMap((filter) =>
            isInputFilter(filter) ? filter : filter.filters
          )
          .filter(
            ({ enabled, filterType }) =>
              enabled === true && filterType !== FilterType.Sort
          )
          .map((filter) => {
            const filterValue = filterValues.find(
              ({ filterId }) => filterId === filter.id
            );

            switch (filter.inputType) {
              case InputType.Select: {
                return (
                  <AppliedSelectPill
                    key={filter.id}
                    filter={filter}
                    filterValue={filterValue as StringFilterValue}
                    filterValues={filterValues}
                    setFilterValues={setFilterValues}
                  />
                );
              }
              case InputType.MultiSelect: {
                return (
                  <AppliedMultiSelectPill
                    key={filter.id}
                    filter={filter}
                    filterValue={filterValue as StringArrayFilterValue}
                    filterValues={filterValues}
                    setFilterValues={setFilterValues}
                  />
                );
              }
              case InputType.Checkbox: {
                return (
                  <AppliedCheckboxPill
                    key={filter.id}
                    filter={filter}
                    filterValues={filterValues}
                    setFilterValues={setFilterValues}
                    filterValue={!!filterValue?.value}
                  />
                );
              }
              default:
                return null;
            }
          })}
      </FilterRowScrollPanel>
    </div>
  );
};
